/**
 * Audit Logs Management Component
 * Main component for audit log monitoring dashboard
 */

import React, { useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
} from "@chakra-ui/react";
import FilterAuditLogs from "./FilterAuditLogs";
import ExportAuditLogs from "./ExportAuditLogs";
import StatsCards from "./StatsCards";
import {
  SuccessFailureChart,
  TimeSeriesChart,
  TopFailedUsersChart,
  TopFailureReasonsChart,
  TopSourcesChart,
} from "./AuditChartsComponents";
import AuditLogsTable from "./AuditLogsTable";
import useAuditLogs from "./useAuditLogs";
import useDashboardAggregations from "./useDashboardAggregations";
import { AuditLogFilters, TimeRangeOption } from "./types";

const AuditLogsManagement: React.FC = () => {
  const bg = useColorModeValue("gray.50", "gray.900");
  const toast = useToast();

  // Audit logs table data
  const {
    data: logsData,
    loading: logsLoading,
    error: logsError,
    filters,
    fetchAuditLogs,
    changePage,
  } = useAuditLogs();

  // Dashboard aggregations
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
  } = useDashboardAggregations(filters);

  // Handle initial fetch
  useEffect(() => {
    fetchAuditLogs();
  }, []);

  // Handle filter changes
  const handleFiltersChange = async (newFilters: AuditLogFilters) => {
    try {
      await fetchAuditLogs(newFilters);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch audit logs",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle page change
  const handlePageChange = async (page: number) => {
    try {
      const newFilters = { ...filters, page };
      await fetchAuditLogs(newFilters);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load page",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Show error if tables fails to load
  useEffect(() => {
    if (logsError) {
      toast({
        title: "Error Loading Audit Logs",
        description: logsError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [logsError, toast]);

  return (
    <Box bg={bg} minH="100vh" p={6}>
      <VStack align="stretch" spacing={6}>
        {/* Filter Section */}
        <FilterAuditLogs
          onFiltersChange={handleFiltersChange}
          loading={logsLoading}
        />

        <Divider />

        {/* Export Section */}
        <HStack justify="flex-end">
          <ExportAuditLogs
            filters={filters}
            timeRange={(filters.time_range as TimeRangeOption) || "last_24_hours"}
            disabled={logsLoading}
          />
        </HStack>

        {/* Stats Cards */}
        {dashboardData && (
          <StatsCards
            stats={dashboardData.stats}
            loading={dashboardLoading}
            timeRange={filters.time_range as TimeRangeOption}
          />
        )}

        <Divider />

        {/* Charts Grid */}
        {dashboardLoading ? (
          <Center py={20}>
            <Spinner />
          </Center>
        ) : dashboardData ? (
          <VStack align="stretch" spacing={6}>
            {/* Row 1: Success/Failure and Time Series */}
            <HStack
              align="stretch"
              spacing={6}
              flexDirection={{ base: "column", lg: "row" }}
            >
              <Box flex={1}>
                <SuccessFailureChart
                  data={dashboardData.success_failure_ratio}
                  loading={dashboardLoading}
                />
              </Box>
              <Box flex={1}>
                <TimeSeriesChart
                  data={dashboardData.time_series}
                  loading={dashboardLoading}
                />
              </Box>
            </HStack>

            {/* Row 2: Top Failed Users */}
            {/* <Box w="100%">
              <TopFailedUsersChart
                data={dashboardData.top_failed_users}
                loading={dashboardLoading}
              />
            </Box> */}

            {/* Row 3: Top Failure Reasons and Top Sources */}
            {/* <HStack
              align="stretch"
              spacing={6}
              flexDirection={{ base: "column", lg: "row" }}
            >
              <Box flex={1}>
                <TopFailureReasonsChart
                  data={dashboardData.top_failure_reasons}
                  loading={dashboardLoading}
                />
              </Box>
              <Box flex={1}>
                <TopSourcesChart
                  data={dashboardData.top_sources}
                  loading={dashboardLoading}
                />
              </Box>
            </HStack> */}
          </VStack>
        ) : null}

        <Divider />

        {/* Audit Logs Table */}
        <Box>
          <AuditLogsTable
            data={logsData}
            loading={logsLoading}
            onPageChange={handlePageChange}
          />
        </Box>
      </VStack>
    </Box>
  );
};

export default AuditLogsManagement;
